import React from 'react';
import { PencilLine } from 'lucide-react';

/**
 * Shared wording for transport legs, so every table says the same thing.
 *
 * A leg is a manual entry when the user typed the distance instead of choosing an origin and a
 * destination. Saved legs carry `manual_distance: true`; legs still in the calculator carry the
 * typed number in `manual_distance`; either way there is no route to show.
 */
export function isManualLeg(leg) {
  if (!leg) return false;
  const from = leg.from_location?.address;
  const to = leg.to_location?.address;
  return Boolean(leg.manual_distance) || (!from && !to);
}

export function shortPlace(address) {
  return (address || '').split(',')[0].trim();
}

const MODE_NAMES = { road: 'Road', rail: 'Rail', air: 'Air', water: 'Sea', sea: 'Sea' };
export const modeName = (mode) => MODE_NAMES[mode] || (mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : '—');

const ACRONYMS = new Set(['hgv', 'hgvs', 'rf', 'lpg', 'cng', 'lng', 'bev', 'phev', 'teu', 'dwt', 'ro', 'uk']);
/** 'hgv_all_hgvs_average' -> 'HGV all HGVs average'; 'long_haul_with_rf' -> 'Long haul with RF'. */
export function vehicleName(key) {
  if (!key) return '';
  if (key === 'freight_train') return 'Freight train';
  const words = String(key).split('_').filter(Boolean).map((w) => {
    const lw = w.toLowerCase();
    if (ACRONYMS.has(lw)) return lw === 'hgvs' ? 'HGVs' : lw.toUpperCase();
    return lw;
  });
  const s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Icon-only marker for dense tables (dashboard): same meaning, no extra text. */
export function ManualDistanceIcon({ className = '' }) {
  const label = 'Distance entered manually';
  return (
    <span
      className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-amber-600 dark:text-amber-400 ${className}`}
      title={label}
    >
      <PencilLine className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Small pill marking a distance the user entered directly (shown on New Shipment only). */
export function ManualDistanceBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-500/25 dark:text-amber-300 ${className}`}
      title="The distance was entered by hand, not calculated from an origin and destination"
    >
      <PencilLine className="h-3 w-3" aria-hidden="true" />
      Manual distance
    </span>
  );
}
